import React from 'react';

import { Typography } from '@mui/material';
import {
  AddressIcon,
  BuildingIcon,
  DateIcon,
  EmailIcon,
  PhoneIcon,
  UserIcon,
} from '@sharedCR/coreDesignSystem/icons';

import { getConditionalArray } from 'utils/data';

import SnapdocsStatusBadge from './components/SnapdocsStatusBadge';
import {
  formatAppointmentDateTime,
  formatAppointmentLocation,
  formatLedgerStatus,
  formatPhone,
  getDisplayValue,
  getPersonFullName,
  hasDisplayValue,
} from './SnapdocsClosingSection.utils';

export const getSummaryRowsConfig = ({ status, appointment, notaryOrder }) => [
  {
    id: 'snapdocsStatus',
    label: 'SnapDocs Status',
    value: <SnapdocsStatusBadge status={status?.snapdocsStatus} />,
  },
  {
    id: 'dispatchStatus',
    label: 'Ledger Status',
    value: <SnapdocsStatusBadge status={formatLedgerStatus(status?.dispatchStatus)} />,
  },
  {
    id: 'signingMethod',
    label: 'Signing Method',
    value: getDisplayValue(status?.signingMethod),
  },
  {
    id: 'appointmentStatus',
    label: 'Appointment Status',
    value: <SnapdocsStatusBadge status={appointment?.status} />,
  },
  {
    id: 'esignOptStatus',
    label: 'eSign Opt Status',
    value: <SnapdocsStatusBadge status={status?.esignOptStatus} />,
  },
  {
    id: 'notaryStatus',
    label: 'Notary Status',
    value: <SnapdocsStatusBadge status={notaryOrder?.status ?? status?.notaryStatus} />,
  },
  ...getConditionalArray(hasDisplayValue(status?.enoteStatus), () => [
    {
      id: 'enoteStatus',
      label: 'eNote Status',
      value: <SnapdocsStatusBadge status={status.enoteStatus} />,
    },
  ]),
  ...getConditionalArray(hasDisplayValue(status?.ronStatus), () => [
    {
      id: 'ronStatus',
      label: 'RON Status',
      value: <SnapdocsStatusBadge status={status.ronStatus} />,
    },
  ]),
];

const getNotaryRows = ({ notaryOrder, notaryErrorMessage }) => {
  if (notaryErrorMessage) {
    return [{ id: 'notaryError', icon: UserIcon, value: notaryErrorMessage }];
  }

  if (!notaryOrder) {
    return [{ id: 'noNotary', icon: UserIcon, value: 'No notary ordered' }];
  }

  return [
    {
      id: 'notaryName',
      icon: UserIcon,
      value: getDisplayValue(notaryOrder.notaryName),
    },
    {
      id: 'notaryCompany',
      icon: BuildingIcon,
      value: getDisplayValue(notaryOrder.notaryCompany),
    },
    {
      id: 'notaryPhone',
      icon: PhoneIcon,
      value: formatPhone(notaryOrder.notaryPhone),
    },
    {
      id: 'notaryEmail',
      icon: EmailIcon,
      value: getDisplayValue(notaryOrder.notaryEmail),
    },
    ...getConditionalArray(hasDisplayValue(notaryOrder.didNotSignReason), () => [
      {
        id: 'didNotSignReason',
        icon: UserIcon,
        value: `Did not sign: ${notaryOrder.didNotSignReason}`,
      },
    ]),
  ];
};

export const getDetailGroupsConfig = ({ status, appointment, notaryOrder, notaryErrorMessage }) => [
  {
    id: 'appointment',
    label: 'Appointment:',
    rows: [
      {
        id: 'appointmentDateTime',
        icon: DateIcon,
        value: formatAppointmentDateTime({ appointmentAt: status?.appointmentAt, appointment }),
      },
      {
        id: 'appointmentLocation',
        icon: AddressIcon,
        value: formatAppointmentLocation(appointment),
      },
    ],
  },
  {
    id: 'notary',
    label: 'Notary:',
    rows: getNotaryRows({ notaryOrder, notaryErrorMessage }),
  },
];

const renderStatusCell = field => row => <SnapdocsStatusBadge status={row[field]} />;

export const borrowersTableConfig = {
  name: {
    label: 'Borrower',
    width: '22%',
    content: borrower => (
      <Typography color="text.secondary" variant="regular14">
        {getPersonFullName(borrower)}
      </Typography>
    ),
  },
  previewStatus: { label: 'Preview', width: '13%', content: renderStatusCell('previewStatus') },
  eSigningStatus: { label: 'eSign', width: '13%', content: renderStatusCell('eSigningStatus') },
  ronPreviewStatus: {
    label: 'RON Preview',
    width: '13%',
    content: renderStatusCell('ronPreviewStatus'),
  },
  ronESigningStatus: {
    label: 'RON eSign',
    width: '13%',
    content: renderStatusCell('ronESigningStatus'),
  },
  kbaStatus: { label: 'KBA', width: '13%', content: renderStatusCell('kbaStatus') },
  idvStatus: { label: 'IDV', width: '13%', content: renderStatusCell('idvStatus') },
};
